import { Mail, MapPin, MessageCircle, ArrowUpRight } from "lucide-react";
import { useLang } from "../../i18n/LanguageContext";

export const Contact = () => {
  const { t } = useLang();

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-16 lg:py-20 border-t border-white/5 overflow-hidden"
    >
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[720px] h-[720px] rounded-full bg-[#2563EB]/15 blur-[160px]" />
        <div className="absolute bottom-0 right-0 w-[520px] h-[520px] rounded-full bg-[#00E5FF]/10 blur-[140px]" />
      </div>

      <div className="max-w-5xl mx-auto px-6 lg:px-10 text-center">
        <div className="inline-flex items-center gap-2 text-[#00E5FF] font-mono text-xs uppercase tracking-[0.24em]">
          <span className="w-6 h-px bg-[#00E5FF]" />
          {t.contact.eyebrow}
          <span className="w-6 h-px bg-[#00E5FF]" />
        </div>
        <h2
          data-testid="contact-title"
          className="mt-6 font-heading font-bold text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight"
        >
          {t.contact.title}
        </h2>
        <p className="mt-6 text-slate-300 text-base sm:text-lg leading-relaxed max-w-2xl mx-auto">
          {t.contact.subtitle}
        </p>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-4 text-left">
          <ContactCard
            testid="contact-location"
            icon={MapPin}
            label={t.contact.company}
            value={t.contact.location}
          />
          <ContactCard
            testid="contact-email"
            icon={Mail}
            label={t.contact.emailLabel}
            value={t.contact.email}
            href={`mailto:${t.contact.email}`}
          />
          <ContactCard
            testid="contact-whatsapp"
            icon={MessageCircle}
            label={t.contact.whatsappLabel}
            value={t.contact.whatsappCta}
            href={t.contact.whatsappUrl}
            external
          />
        </div>

        <p className="mt-10 text-xs font-mono uppercase tracking-widest text-slate-500 max-w-xl mx-auto">
          {t.contact.note}
        </p>
      </div>
    </section>
  );
};

function ContactCard({ icon: Icon, label, value, href, external, testid }) {
  const inner = (
    <>
      <div className="flex items-start justify-between">
        <span className="inline-flex items-center justify-center w-10 h-10 border border-[#00E5FF]/30 bg-[#00E5FF]/5 rounded-sm">
          <Icon size={18} strokeWidth={1.5} className="text-[#00E5FF]" />
        </span>
        {href && (
          <ArrowUpRight
            size={16}
            strokeWidth={1.5}
            className="text-slate-500 group-hover:text-[#00E5FF] transition-colors duration-200"
          />
        )}
      </div>
      <div className="mt-6 font-mono text-[10px] uppercase tracking-widest text-slate-500">
        {label}
      </div>
      <div className="mt-1.5 text-white font-heading font-bold text-lg leading-snug break-words">
        {value}
      </div>
    </>
  );

  const classes =
    "group block border border-white/10 bg-[#151B2B] p-6 rounded-sm hover:border-[#00E5FF]/50 hover:-translate-y-1 transition-[transform,border-color] duration-300 text-left";

  if (href) {
    return (
      <a
        href={href}
        data-testid={testid}
        target={external ? "_blank" : undefined}
        rel={external ? "noopener noreferrer" : undefined}
        className={classes}
      >
        {inner}
      </a>
    );
  }
  return (
    <div data-testid={testid} className={classes}>
      {inner}
    </div>
  );
}

export default Contact;
